import { useState } from "react";
import { Link } from "react-router";
import { Aviso, Botao, Cartao } from "../ui";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  useDescartarDaQuarentena,
  useLiberarDaQuarentena,
  useOrganizacoesParaCanais,
  useQuarentena,
  type EmailRecebido,
} from "../../nucleo/canais";
import { quandoLegivel } from "../../nucleo/automacao";
import { CampoDeEscolha } from "../automacao/apoio";

function ItemDaQuarentena({ item, aoDecidir }: { item: EmailRecebido; aoDecidir: (frase: string) => void }) {
  const organizacoes = useOrganizacoesParaCanais();
  const liberar = useLiberarDaQuarentena();
  const descartar = useDescartarDaQuarentena();
  const [organizacaoId, setOrganizacaoId] = useState("");
  const [confirmando, setConfirmando] = useState(false);

  const falha = liberar.error ?? descartar.error;

  return (
    <Cartao titulo={item.assunto || "(sem assunto)"} data-testid={`quarentena-${item.id}`}>
      <p>
        De <code>{item.remetente ?? "remetente ilegível"}</code> · recebido em {quandoLegivel(item.recebido_em)}
      </p>
      {item.motivo ? (
        <Aviso tom="atencao" className="mt-2">
          <b>Por que ficou aqui:</b> {item.motivo}
        </Aviso>
      ) : null}
      <details className="mt-2">
        <summary>Ler a mensagem</summary>
        <pre className="texto-preservado" data-testid="corpo-da-quarentena">{item.corpo_texto || "(sem texto)"}</pre>
      </details>
      {item.numero_do_ticket && item.ticket_id ? (
        <p className="nota-rodape mt-2">
          A mensagem citava o chamado <Link to={`/admin/chamados/${item.ticket_id}`}>#{item.numero_do_ticket}</Link>.
        </p>
      ) : null}
      <div className="mt-3">
        <CampoDeEscolha
          rotulo="Abrir chamado novo na empresa"
          valor={organizacaoId}
          aoMudar={setOrganizacaoId}
          vazio="Escolha a empresa"
          opcoes={(organizacoes.data ?? []).map((o) => ({ valor: o.id, rotulo: o.nome_fantasia ?? o.razao_social }))}
        />
        <div className="flex gap-2 flex-wrap">
          <Botao
            type="button"
            disabled={!organizacaoId || liberar.isPending}
            aria-label={`Abrir chamado com a mensagem de ${item.remetente ?? "remetente ilegível"}`}
            onClick={() =>
              liberar.mutate(
                { id: item.id, corpo: { organizacao_id: organizacaoId } },
                { onSuccess: (r) => aoDecidir(`Chamado #${r.numero} aberto.`) },
              )
            }
          >
            Abrir chamado
          </Botao>
          {item.ticket_id ? (
            <Botao
              type="button"
              variante="fantasma"
              disabled={liberar.isPending}
              onClick={() =>
                liberar.mutate(
                  { id: item.id, corpo: { ticket_id: item.ticket_id! } },
                  { onSuccess: (r) => aoDecidir(`Mensagem acrescentada ao chamado #${r.numero}.`) },
                )
              }
            >
              Acrescentar ao chamado citado
            </Botao>
          ) : null}
          {confirmando ? (
            <>
              <Botao
                type="button"
                variante="solido"
                disabled={descartar.isPending}
                onClick={() => descartar.mutate(item.id, { onSuccess: () => aoDecidir("Mensagem descartada.") })}
              >
                Confirmar o descarte
              </Botao>
              <Botao type="button" variante="fantasma" onClick={() => setConfirmando(false)}>
                Manter
              </Botao>
            </>
          ) : (
            <Botao type="button" variante="fantasma" onClick={() => setConfirmando(true)}>
              Descartar
            </Botao>
          )}
        </div>
      </div>
      {falha ? <Aviso tom="erro" className="mt-2">{mensagemDoConsole(falha)}</Aviso> : null}
    </Cartao>
  );
}

export function PainelDaQuarentena() {
  const quarentena = useQuarentena();
  /* O aviso do que a equipe acabou de decidir vive AQUI, e não no cartão: decidir tira o item da
     fila, o cartão some, e um aviso preso a ele sumiria junto antes de alguém ler. */
  const [decididos, setDecididos] = useState<string[]>([]);
  return (
    <section aria-labelledby="titulo-quarentena">
      <h2 className="titulo-secao" id="titulo-quarentena">Em quarentena</h2>
      <p className="nota-rodape mb-3">
        E-mails que o portal não soube acolher sozinho: remetente desconhecido, sem prova de
        autenticação ou de outra empresa que não a do chamado citado. Nada daqui chegou a um chamado.
      </p>
      <div role="status" data-testid="decisoes-da-quarentena">
        {decididos.map((frase, indice) => (
          <Aviso key={`${indice}-${frase}`} tom="sucesso" className="mb-2">{frase}</Aviso>
        ))}
      </div>
      {quarentena.isPending ? <p role="status">Carregando a quarentena…</p> : null}
      {quarentena.isError ? <Aviso tom="erro">{mensagemDoConsole(quarentena.error)}</Aviso> : null}
      {quarentena.data && quarentena.data.length === 0 ? (
        <Aviso tom="sucesso" data-testid="quarentena-vazia">Nada esperando decisão.</Aviso>
      ) : null}
      <div className="lista-de-itens" data-testid="lista-da-quarentena">
        {quarentena.data?.map((item) => (
          <ItemDaQuarentena key={item.id} item={item} aoDecidir={(frase) => setDecididos((atuais) => [frase, ...atuais].slice(0, 5))} />
        ))}
      </div>
    </section>
  );
}
